import { useRef, useCallback } from 'react';

import logger from '@/utils/logger';
import RtcClient from '@/lib/RtcClient';
import store from '@/store';

export interface AutoQuestionConfig {
  clearUrlAfterSend?: boolean;
  maxRetries?: number;
  retryDelays?: number[]; // ms
  readinessTimeout?: number; // ms
  readinessPollInterval?: number; // ms
}

export interface AutoQuestionCallbacks {
  onSendQuestion?: (question: string) => Promise<void>;
  onQuestionSent?: (question: string) => void;
  onError?: (error: Error, question: string) => void;
}

const DEFAULT_CONFIG: Required<AutoQuestionConfig> = {
  clearUrlAfterSend: true,
  maxRetries: 3,
  retryDelays: [200, 500, 1000],
  readinessTimeout: 4000,
  readinessPollInterval: 150,
};

/**
 * 简化的自动提问 Hook
 *
 * 功能说明：
 * - 提供一个可被外部调用的发送函数
 * - 确保同一个问题只发送一次
 * - 发送后可选择性清除 URL 参数
 */
export const useAutoQuestion = (
  question: string | null,
  config: AutoQuestionConfig = {},
  callbacks: AutoQuestionCallbacks = {}
) => {
  const hasAutoQuestionSent = useRef(false);
  const lastQuestionRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const attemptsRef = useRef(0);

  // 使用ref来稳定回调函数引用
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const { onSendQuestion, onQuestionSent, onError } = callbacksRef.current;

  // 清除 URL 参数
  const clearUrlParams = useCallback(() => {
    if (!finalConfig.clearUrlAfterSend) return;

    try {
      if (typeof window !== 'undefined' && window.history) {
        const url = new URL(window.location.href);
        url.searchParams.delete('Question'); // 注意大写的Q，与URL参数保持一致
        url.searchParams.delete('DirectRoom'); // 也删除DirectRoom参数
        // 保留layout2 / layout3等其他重要参数
        window.history.replaceState({}, '', url.toString());
      }
    } catch (error) {
      logger.warn('[AutoQuestion] 清除URL参数失败:', error);
    }
  }, [finalConfig.clearUrlAfterSend]);

  // 等待系统就绪（RTC连接、房间、botName等）
  const waitForReadiness = useCallback(async (): Promise<boolean> => {
    const timeout = finalConfig.readinessTimeout;
    const interval = finalConfig.readinessPollInterval;
    const start = Date.now();

    const isReadyNow = () => {
      try {
        const rtcReady = !!RtcClient?.engine && !!RtcClient?.basicInfo?.room_id;
        const state = store.getState()?.room;
        const scene = state?.scene;
        const sceneConfigMap = state?.sceneConfigMap;
        const botName = scene && sceneConfigMap ? sceneConfigMap[scene]?.botName : null;
        return rtcReady && !!botName;
      } catch {
        return false;
      }
    };

    if (isReadyNow()) return true;

    return new Promise<boolean>((resolve) => {
      const tick = () => {
        if (isReadyNow()) {
          resolve(true);
        } else if (Date.now() - start >= timeout) {
          resolve(false);
        } else {
          setTimeout(tick, interval);
        }
      };
      setTimeout(tick, interval);
    });
  }, [finalConfig.readinessTimeout, finalConfig.readinessPollInterval]);

  // 触发自动提问的核心函数
  const triggerAutoQuestion = useCallback(async () => {
    const q = (question || '').trim();
    if (!q) {
      logger.info('[AutoQuestion] 跳过发送：无有效问题');
      return;
    }
    if (hasAutoQuestionSent.current) {
      logger.info('[AutoQuestion] 已发送过自动提问，跳过');
      return;
    }
    if (lastQuestionRef.current === q) {
      logger.info('[AutoQuestion] 与上次问题相同且已尝试发送，跳过');
      return;
    }
    if (inFlightRef.current) {
      logger.info('[AutoQuestion] 已在发送流程中，合并触发');
      return;
    }
    if (!onSendQuestion) {
      logger.warn('[AutoQuestion] 未提供发送回调函数');
      return;
    }

    inFlightRef.current = true;
    attemptsRef.current = 0;
    lastQuestionRef.current = q;

    const attemptOnce = async (): Promise<boolean> => {
      attemptsRef.current += 1;
      const attemptNo = attemptsRef.current;

      // 就绪检查
      const ready = await waitForReadiness();
      if (!ready) {
        logger.warn('[AutoQuestion] 就绪检查超时，尝试继续发送（可能失败）');
      }

      try {
        logger.info(`[AutoQuestion] 第${attemptNo}次尝试发送自动提问:`, q);
        await onSendQuestion(q);

        hasAutoQuestionSent.current = true;

        // 清除URL参数
        clearUrlParams();

        // 成功回调
        onQuestionSent?.(q);
        logger.info('[AutoQuestion] 自动提问发送成功:', q);
        return true;
      } catch (error) {
        logger.warn(`[AutoQuestion] 第${attemptNo}次发送失败:`, error);
        if (error instanceof Error) {
          onError?.(error, q);
        }
        return false;
      }
    };

    // 首次尝试
    let ok = await attemptOnce();

    // 重试（指数退避/自定义延迟）
    while (!ok && attemptsRef.current <= finalConfig.maxRetries) {
      const idx = attemptsRef.current - 1;
      const delay =
        finalConfig.retryDelays[idx] ?? finalConfig.retryDelays[finalConfig.retryDelays.length - 1];
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(resolve, delay);
      });
      // eslint-disable-next-line no-await-in-loop
      ok = await attemptOnce();
    }

    if (!ok) {
      // 所有尝试失败，允许后续外部再次触发
      logger.error('[AutoQuestion] 所有重试均失败，保留未发送状态以便后续触发');
      hasAutoQuestionSent.current = false;
    }

    inFlightRef.current = false;
  }, [
    question,
    onSendQuestion,
    onQuestionSent,
    onError,
    clearUrlParams,
    finalConfig.maxRetries,
    finalConfig.retryDelays,
    waitForReadiness,
  ]);

  // 重置发送状态（用于调试或特殊情况）
  const resetAutoQuestionState = useCallback(() => {
    hasAutoQuestionSent.current = false;
    logger.info('[AutoQuestion] 发送状态已重置');
  }, []);

  return {
    // 核心功能：外部调用这个函数来触发自动提问
    triggerAutoQuestion,

    // 状态查询：外部可以查询是否已经发送过
    hasSent: hasAutoQuestionSent.current,

    // 工具函数：重置状态和清除URL参数
    resetAutoQuestionState,
    clearUrlParams,
  };
};

export default useAutoQuestion;
